/**
 * Shared tree traversal utilities for Lexical EditorState export.
 *
 * All three export functions (entities, HTML, MarkdownV2) use the same
 * tree walking logic to extract text segments with their formatting context.
 */
import {
  $getRoot,
  $isTextNode,
  $isLineBreakNode,
  $isElementNode,
  type EditorState,
  type TextNode,
  type LexicalNode,
} from 'lexical';
import { $isLinkNode } from '@lexical/link';
import { $isCodeNode } from '@lexical/code';
import { $isQuoteNode } from '@lexical/rich-text';
import {
  $isSpoilerNode,
  $isExpandableQuoteNode,
  $isCustomEmojiNode,
} from '../nodes';

/**
 * Element context entry describing an ancestor element that contributes
 * an entity wrapper to the text segments below it.
 */
export interface ElementContext {
  type:
    | 'spoiler'
    | 'blockquote'
    | 'expandable_blockquote'
    | 'code_block'
    | 'link'
    | 'custom_emoji';
  /** For code_block: programming language (optional) */
  language?: string;
  /** For link: URL (unless it's a tg://user link) */
  url?: string;
  /** For link: user ID if it's a tg://user?id=... mention */
  userId?: number;
  /** For custom_emoji: the custom emoji ID */
  customEmojiId?: string;
  /** For custom_emoji: fallback text representation */
  fallback?: string;
  /** Unique key to identify this context entry for tracking open entities */
  key: string;
}

/** Inline format types that map to Telegram entities */
export type InlineFormat =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'code';

/**
 * A segment represents a contiguous run of text with uniform formatting.
 * The tree walker emits a flat array of these segments.
 */
export interface TextSegment {
  /** The raw text content of this segment */
  text: string;

  /** Active inline formats on this segment (from TextNode format flags) */
  formats: Set<InlineFormat>;

  /**
   * Stack of ancestor element nodes that contribute entity wrappers.
   * Ordered from outermost to innermost.
   */
  elementContext: ElementContext[];

  /** True if this segment is a paragraph separator (newline) */
  isSeparator: boolean;
}

/** Counter for generating unique keys for element contexts */
let elementKeyCounter = 0;

/**
 * Extracts inline format flags from a TextNode.
 */
function extractFormats(node: TextNode): Set<InlineFormat> {
  const formats = new Set<InlineFormat>();
  if (node.hasFormat('bold')) formats.add('bold');
  if (node.hasFormat('italic')) formats.add('italic');
  if (node.hasFormat('underline')) formats.add('underline');
  if (node.hasFormat('strikethrough')) formats.add('strikethrough');
  if (node.hasFormat('code')) formats.add('code');
  return formats;
}

/**
 * Parses a tg://user?id=... URL and returns the user ID, or null if invalid.
 */
function parseTelegramUserLink(url: string): number | null {
  const match = /^tg:\/\/user\?id=(\d+)$/i.exec(url);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * Recursively walks a Lexical node and its descendants, emitting TextSegments.
 */
function walkNode(
  node: LexicalNode,
  parentContext: ElementContext[],
  segments: TextSegment[]
): void {
  if ($isTextNode(node)) {
    const formats = extractFormats(node);
    const text = node.getTextContent();
    if (text.length > 0) {
      segments.push({
        text,
        formats,
        elementContext: parentContext,
        isSeparator: false,
      });
    }
  } else if ($isLineBreakNode(node)) {
    segments.push({
      text: '\n',
      formats: new Set(),
      elementContext: parentContext,
      isSeparator: true,
    });
  } else if ($isCustomEmojiNode(node)) {
    // CustomEmojiNode is a DecoratorNode, handle it specially
    const emojiNode = node;
    const fallback = emojiNode.__fallback;
    const context: ElementContext[] = [
      ...parentContext,
      {
        type: 'custom_emoji',
        customEmojiId: emojiNode.__emojiId,
        fallback,
        key: `custom_emoji-${String(elementKeyCounter++)}`,
      },
    ];
    if (fallback.length > 0) {
      segments.push({
        text: fallback,
        formats: new Set(),
        elementContext: context,
        isSeparator: false,
      });
    }
  } else if ($isElementNode(node)) {
    const context: ElementContext[] = [...parentContext];

    // Build element context based on node type
    if ($isSpoilerNode(node)) {
      context.push({
        type: 'spoiler',
        key: `spoiler-${String(elementKeyCounter++)}`,
      });
    } else if ($isExpandableQuoteNode(node)) {
      context.push({
        type: 'expandable_blockquote',
        key: `expandable_blockquote-${String(elementKeyCounter++)}`,
      });
    } else if ($isQuoteNode(node)) {
      context.push({
        type: 'blockquote',
        key: `blockquote-${String(elementKeyCounter++)}`,
      });
    } else if ($isCodeNode(node)) {
      const codeNode = node;
      context.push({
        type: 'code_block',
        language: codeNode.getLanguage() ?? undefined,
        key: `code_block-${String(elementKeyCounter++)}`,
      });
    } else if ($isLinkNode(node)) {
      const linkNode = node;
      const url = linkNode.getURL();
      const userId = parseTelegramUserLink(url);
      if (userId !== null) {
        context.push({
          type: 'link',
          userId,
          key: `link-${String(elementKeyCounter++)}`,
        });
      } else {
        context.push({
          type: 'link',
          url,
          key: `link-${String(elementKeyCounter++)}`,
        });
      }
    }

    // Walk children
    for (const child of node.getChildren()) {
      walkNode(child, context, segments);
    }
  }
}

/**
 * Walks the entire Lexical EditorState and returns a flat array of TextSegments.
 *
 * @param editorState - The Lexical editor state to walk
 * @returns Array of TextSegments representing the document content
 */
export function walkTree(editorState: EditorState): TextSegment[] {
  // Reset the counter for each walk
  elementKeyCounter = 0;
  const segments: TextSegment[] = [];

  editorState.read(() => {
    const root = $getRoot();
    const children = root.getChildren();

    for (const child of children) {
      // Add paragraph separator between top-level children
      if (segments.length > 0) {
        segments.push({
          text: '\n',
          formats: new Set(),
          elementContext: [],
          isSeparator: true,
        });
      }
      walkNode(child, [], segments);
    }
  });

  return segments;
}

/**
 * Checks if a segment is inside a code block context.
 */
export function isInCodeBlock(segment: TextSegment): boolean {
  return segment.elementContext.some((ctx) => ctx.type === 'code_block');
}

/**
 * Gets the innermost code block context from a segment, if any.
 */
export function getCodeBlockContext(
  segment: TextSegment
): ElementContext | null {
  for (let i = segment.elementContext.length - 1; i >= 0; i--) {
    const ctx = segment.elementContext[i];
    if (ctx && ctx.type === 'code_block') {
      return ctx;
    }
  }
  return null;
}
